import { useModal } from "@ebay/nice-modal-react";
import { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import FlashContext from "components/FlashMessages/FlashContext";
import FormFieldClaimMapping from "components/FormFields/ClaimMapping";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerSection from "ds/components/Drawer/Section";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import FormField from "ds/components/Form/Field";
import FormToggleField from "ds/components/Form/ToggleField";
import Input from "ds/components/Input";
import Select from "ds/components/Select";
import Textarea from "ds/components/Textarea";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import { createDefaultMutationHook } from "hooks/useMutationHandler";
import useTypedContext from "hooks/useTypedContext";
import setLocation from "shared/setLocation";
import { stringIsRequired, validateRequiredURL } from "utils/formValidators";
import { getDrawerFormFix } from "utils/css";

import SettingsSingleSignOnConfigInfoSAML from "../../components/ConfigInfoSAML";
import { SAML_NAME_ID_OPTIONS } from "../constants";
import { CREATE_SSO_SAML } from "./gql";
import { formDefaultValues, type SettingsSingleSignOnSAMLFormFields } from "./helpers";

const useSAMLCreate = createDefaultMutationHook(CREATE_SSO_SAML);

type SettingsSingleSignOnCreateSAMLProps = {
  setIsDirty: (isDirty: boolean) => void;
};

const SettingsSingleSignOnCreateSAML = ({ setIsDirty }: SettingsSingleSignOnCreateSAMLProps) => {
  const { reportSuccess } = useTypedContext(FlashContext);
  const modal = useModal();

  const [createSAML, { loading }] = useSAMLCreate({
    refetchQueries: ["GetSSOSettings"],
  });

  const form = useForm<SettingsSingleSignOnSAMLFormFields>({
    mode: "onChange",
    defaultValues: formDefaultValues,
  });

  const {
    register,
    formState: { errors, isDirty },
    control,
    watch,
    handleSubmit,
    trigger,
  } = form;

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationSAML,

    defaultCallbackTrackProperties: {
      method: "SAML",
    },
  });

  const watchDynamicFieldValue = watch("dynamic");
  const watchMetadataFieldValue = watch("metadata");

  const onSubmit = handleSubmit((data) => {
    trackSegmentAnalyticsEvent("Set Up Save");

    const filteredClaimMapping = data.claimMapping.filter(
      (claim) => claim.name.trim() && claim.value.trim()
    );

    createSAML(
      {
        dynamic: data.dynamic,
        metadata: data.metadata,
        nameIDFormat: data.nameIDFormat,
        claimMapping: filteredClaimMapping.length > 0 ? { entries: filteredClaimMapping } : null,
      },
      (data) => {
        if (data?.samlCreate) {
          // FYI: `/saml/start` is BE endpoint, do not replace it with "useNavigate", it works only with navigating to the external URL
          setLocation("/saml/start");
          reportSuccess({ message: "SAML successfully enabled" });
          modal.hide();
        }
      }
    );
  });

  useEffect(() => {
    if (isDirty && !!watchMetadataFieldValue) {
      trigger("metadata");
    }
  }, [isDirty, trigger, watchDynamicFieldValue, watchMetadataFieldValue]);

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  return (
    <FormProvider {...form}>
      <DrawerBody hasStickyFooter noPadding>
        <form onSubmit={onSubmit} {...getDrawerFormFix()}>
          <DrawerSection gap="x-large">
            <SettingsSingleSignOnConfigInfoSAML />
          </DrawerSection>
          <DrawerSection gap="x-large">
            <Controller
              name="nameIDFormat"
              control={control}
              rules={{ required: "NameID format is required" }}
              render={({ field, fieldState }) => (
                <FormField label="NameID format" error={fieldState.error?.message} noMargin>
                  {({ ariaInputProps }) => (
                    <Select
                      items={SAML_NAME_ID_OPTIONS}
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error?.message}
                      {...ariaInputProps}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="dynamic"
              control={control}
              render={({ field }) => (
                <FormToggleField
                  title="Dynamic configuration"
                  variant="switch"
                  onChange={field.onChange}
                  checked={field.value}
                />
              )}
            />

            {watchDynamicFieldValue ? (
              <FormField label="IdP metadata URL" error={errors?.metadata?.message} noMargin>
                {({ ariaInputProps }) => (
                  <Input
                    type="url"
                    placeholder="https://"
                    error={!!errors?.metadata}
                    {...register("metadata", {
                      validate: validateRequiredURL(),
                      setValueAs: (value: string) => value.trim(),
                    })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>
            ) : (
              <FormField label="IdP metadata" error={errors?.metadata?.message} noMargin>
                {({ ariaInputProps }) => (
                  <Textarea
                    placeholder="Static XML identity provider metadata"
                    error={!!errors?.metadata}
                    {...register("metadata", {
                      validate: stringIsRequired(),
                    })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>
            )}
          </DrawerSection>
          <DrawerSection>
            <FormFieldClaimMapping />
          </DrawerSection>

          <DrawerFooter fullWidth sticky>
            <DrawerFooterActions>
              <DrawerCancelButton />
              <Button variant="primary" type="submit" loading={loading}>
                Save
              </Button>
            </DrawerFooterActions>
          </DrawerFooter>
        </form>
      </DrawerBody>
    </FormProvider>
  );
};

export default SettingsSingleSignOnCreateSAML;
